import { API, GraphQLQuery } from "@aws-amplify/api";

/**
 * Use this function to get the full list of results from a list query, regardless of pagination limits.
 * @type QueryType - The type generated for the query (ex: ListUsersQuery)
 * @type DataItemType - The type generated for the objects that will be returned (ex: User)
 * @type QueryResultType - Tye type generated for the array returned by the query (ex: ModelUserConnection)
 * @param query The query that will be run (ex: queries.listUsers).
 * @param dataIndex The name of the object returned from the query (ex: "listUsers").
 * @param itemsIndex The name of the items index (usually just "items");
 * @param predicate (optional) The object of the predicate to be passed in with the query (ex: { filter: { email: { eq: "test@email.com" }}}).
 * @param unauthed (optional) If true, will attempt to use API key instead of Cognito Pool for auth.
 * @example
 * const allUsers = await listQueryAutoPaginated<
 *  ListUsersQuery,
 *  User,
 *  ModelUserConnection
 * >(
 *  queries.listUsers,
 *  "listUsers",
 *  "items",
 *  {
 *    // Note: limit is shown here only for illustrative purposes,
 *    // this function will return ALL results for all pages.
 *    limit: 10,
 *    filter: {
 *      email: {
 *        contains: "test.com"
 *      }
 *    }
 *  }
 * );
 * @returns An array of the requested items.
 */
type GraphQLAuthMode = "API_KEY" | "AWS_IAM" | "OPENID_CONNECT" | "AMAZON_COGNITO_USER_POOLS" | "AWS_LAMBDA";

export async function listQueryAutoPaginated<
  // ex: ListUserInvitationsQuery
  QueryType,
  // ex: UserInvitation
  DataItemType,
  // ex: ModelUserInvitationConnection
  QueryResultType
>(
  query: string,
  dataIndex: keyof QueryType,
  itemsIndex: keyof QueryResultType,
  predicate?: any,
  useApiKey?: boolean
): Promise<DataItemType[] | null> {
  let toReturn: DataItemType[] = [];
  let pageCount = 0;
  const MAX_PAGES = 1000; // Safety limit to prevent infinite loops
  const seenTokens = new Set<string | undefined>(); // Track seen nextTokens to detect loops

  const getNextPage = async (nextToken?: string): Promise<DataItemType[]> => {
    // Safety check to prevent infinite loops
    if (pageCount >= MAX_PAGES) {
      return toReturn;
    }
    
    // Check if we've seen this token before (infinite loop detection)
    if (nextToken !== undefined && seenTokens.has(nextToken)) {
      return toReturn;
    }
    
    if (nextToken !== undefined) {
      seenTokens.add(nextToken);
    }
    
    pageCount++;

    // Because we have to define "input" for the lambda the nexttoken must be put one level deeper than a standard list query.
    if (predicate && predicate.input) {
      predicate.input.nextToken = nextToken;
    }
    const fullPredicate = { ...predicate, nextToken: nextToken };
    const results = (await API.graphql<GraphQLQuery<QueryType>>({
      query: query,
      variables: { ...fullPredicate },
      authMode: (useApiKey ? "API_KEY" : "AMAZON_COGNITO_USER_POOLS") as GraphQLAuthMode,
    })) as { data: QueryType };
    const { data } = results;
    const dataChildren = data[dataIndex] as QueryResultType;
    const items = dataChildren[itemsIndex] as DataItemType[];
    const newNextToken = dataChildren["nextToken" as keyof QueryResultType];

    // Safety check: if we got no items and there's a nextToken, something is wrong
    if (items.length === 0 && newNextToken) {
      return toReturn;
    }

    toReturn = [...toReturn, ...items];

    // Only continue if we have a valid nextToken, it's different from the current one, and we haven't seen it before
    if (newNextToken && 
        newNextToken !== nextToken && 
        typeof newNextToken === "string" &&
        !seenTokens.has(newNextToken)) {
      return await getNextPage(String(newNextToken));
    } else {
      return toReturn;
    }
  };

  const finalResults = await getNextPage();
  return finalResults && finalResults.length > 0 ? finalResults : null;
}

/**
 * An all-use function for single-item queries or mutations.
 * @type QueryType - The type generated for the query or mutation (ex: GetUserQuery)
 * @type DataItemType - the type generated for the object that will be returned (ex: User)
 * @param query The query or mutation that will be run (ex: queries.getUser)
 * @param dataIndex The name of the object returned from the query or mutation (ex: "getUser")
 * @param predicate (optional) The object of the predicate to be passed in with the query or mutation (ex: { id: "12345" });
 * @param unauthed (optional) If true, will attempt to use API key instead of Cognito Pool for auth.
 * @example
 * const user = await genericQuery<
 *  GetUserQuery,
 *  User,
 * >(
 *  queries.getUser,
 *  "getUser",
 *  { id: "12345" }
 * )
 * @returns The requested item.
 */
export async function genericQuery<
  // The type of the Query or Mutation (from codegen ex: GetUserQuery or UpdateUserMutation)
  QueryType,
  // The type of the return (from codegen ex: User)
  DataItemType
>(
  // query or mutation from queries.ts or mutations.ts
  query: string,
  // the index of the return (ex: "getUser" or "updateUser")
  dataIndex: keyof QueryType,
  // the object passed in to filter or set fields
  predicate?: object,
  // optional. if true, will attempt to use API key as auth mode.
  useApiKey?: boolean
): Promise<DataItemType | null> {
  const results = (await API.graphql<GraphQLQuery<QueryType>>({
    query: query,
    variables: predicate,
    authMode: (useApiKey ? "API_KEY" : "AMAZON_COGNITO_USER_POOLS") as GraphQLAuthMode,
  })) as { data: QueryType };
  const { data } = results;
  const dataChildren = data[dataIndex] as DataItemType;
  return dataChildren || null;
}

export async function searchQuery<QueryType, QueryResultType>(
  query: string,
  dataIndex: keyof QueryType,
  predicate?: object,
  useApiKey?: boolean
): Promise<QueryResultType | null> {
  const results = (await API.graphql<GraphQLQuery<QueryType>>({
    query: query,
    authMode: (useApiKey ? "API_KEY" : "AMAZON_COGNITO_USER_POOLS") as GraphQLAuthMode,
    variables: predicate
  })) as { data: QueryType };
  const { data } = results;
  const dataChildren = data[dataIndex] as QueryResultType;
  return dataChildren;
}

/**
 * Fetch all items from a REST endpoint with automatic pagination.
 * Uses limit=500 per request and automatically fetches all batches until complete.
 * @param endpoint The REST endpoint name (e.g., "institutions-list", "programs-list", "networks-list")
 * @param dataKey The key in the response that contains the array (e.g., "institutions", "programs", "networks")
 * @param limit Maximum items per request (default: 500)
 * @returns An array of all items from all pages
 */
export async function fetchAllWithPagination<T>(
  endpoint: string,
  dataKey: string,
  limit: number = 500
): Promise<T[] | null> {
  let allItems: T[] = [];
  let nextOffset: string | null = null;

  do {
    const params: any = { limit: String(limit) };
    if (nextOffset) {
      params.nextToken = nextOffset;
    }

    const response = await API.get("simpl-api", endpoint, {
      queryStringParameters: params
    });

    const items = response[dataKey] || [];
    allItems = [...allItems, ...items];

    nextOffset = response.pagination?.nextOffset || null;
  } while (nextOffset);

  return allItems.length > 0 ? allItems : null;
}
